import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { EmployerSubscriptionFeedback } from '@restworld/utility-types';
import SubscriptionCancellationFeedback from './SubscriptionCancellationFeedback';
import { useAuth } from '@restworld/feature-authentication';
import { Modal, ModalContent, SelectOptionType, Button, Link } from '@restworld/ui-ds-base';
import { CheckCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import useCancelSlotsMutation from './flexible-slots-v2/hooks/use-cancel-slots-mutation';
import SubscriptionCancellationSuccessScreen from './flexible-slots-v2/subscription-cancellation-success-screen';
import { useShowOrganizationData } from './organisations/hooks/use-show-organisation-query';

import { cn } from '@restworld/utils-common';
type Props = {
  isOpen: boolean;
  onClose: () => void;
  refresh?: () => void;
  stripeSubscriptionId?: string;
  cancellingFlexibleSlotIds?: number[];
};

const MANAGER_GOOGLE_CALENDAR_LINK =
  'https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ0re_N6JK6U0VfofpKWU1DT3A-HbiJzhQBGr1SrKqNiBCVY0C3JRfsgagFDgmM-yLNqtmOKBf1E'; // Alessandra’s calendar

export default function SubscriptionFeedbackModal({
  isOpen,
  onClose,
  refresh,
  stripeSubscriptionId,
  cancellingFlexibleSlotIds,
}: Props) {
  const { user: loggedInUser } = useAuth();
  const { t } = useTranslation('payment-and-invoices');
  const { mutate, isPending: isLoading, isSuccess, reset: resetMutation } = useCancelSlotsMutation();
  const organisationData = useShowOrganizationData();
  const advisorGoogleCalendarLink = organisationData?.assigned_to_rw_member?.google_calendar_link;
  const fieldNeccessaryMessage = t('the_field_is_necesary');

  const subscriptionFeedbackSchema = Yup.object().shape({
    service_satisfaction: Yup.string().required(fieldNeccessaryMessage),
    motive_for_cancelling_subscription: Yup.object().required(fieldNeccessaryMessage),
    assistance_satisfaction: Yup.string().required(fieldNeccessaryMessage),
    reactivate_service: Yup.string().required(fieldNeccessaryMessage),
    service_referral_rating: Yup.string().required(fieldNeccessaryMessage),
  });

  const methods = useForm<Partial<EmployerSubscriptionFeedback>>({
    resolver: yupResolver(subscriptionFeedbackSchema),
    defaultValues: {},
  });

  const { handleSubmit, reset } = methods;

  const handleLocalOnClose = useCallback(() => {
    reset({});
    resetMutation();
    onClose();
  }, [reset, resetMutation, onClose]);

  const onSubmit: SubmitHandler<
    Partial<EmployerSubscriptionFeedback & { motive_for_cancelling_subscription: SelectOptionType }>
  > = useCallback(
    (data) => {
      const finalData = {
        ...data,
        motive_for_cancelling_subscription: data.motive_for_cancelling_subscription?.key,
      };
      mutate({
        createFeedback: true,
        stripeSubscriptionId,
        flexibleSlotIds: cancellingFlexibleSlotIds,
        employerSubscriptionFeedback: {
          ...finalData,
          organization_id: loggedInUser?.organization_id,
        },
      });
    },
    [mutate, stripeSubscriptionId, cancellingFlexibleSlotIds, loggedInUser?.organization_id]
  );

  const handleTalkToAdvisor = useCallback(
    () => window.open(advisorGoogleCalendarLink || MANAGER_GOOGLE_CALENDAR_LINK, '_blank', 'noopener,noreferrer'),
    [advisorGoogleCalendarLink]
  );

  const handleTalkToManager = useCallback(
    () => window.open(MANAGER_GOOGLE_CALENDAR_LINK, '_blank', 'noopener,noreferrer'),
    []
  );

  const modalFooterEl = useMemo(
    () => (
      <ModalFooterWrapper className={isSuccess ? 'lg:flex-col' : ''}>
        <Button
          type="button"
          onClick={isSuccess ? handleTalkToAdvisor : handleSubmit(onSubmit)}
          loading={isLoading}
          disabled={isLoading}
          className={'flex-1'}
          variant={isSuccess ? 'primary' : 'secondary-danger'}
          EndIcon={isSuccess ? undefined : TrashIcon}
        >
          {isSuccess ? t('talk_to_your_advisor') : t('cancel_slot_key')}
        </Button>
        {isSuccess && (
          <Button variant="secondary-restworld" onClick={handleTalkToManager}>
            {t('talk_to_a_manager')}
          </Button>
        )}
        {isSuccess ? (
          <Link onClick={handleLocalOnClose} newTab={false} className={'w-full text-center justify-center'}>
            {t('no_thank_you')}
          </Link>
        ) : (
          <Button onClick={handleLocalOnClose} variant="tertiary" className={'flex-1'}>
            {t('do_not_cancel')}
          </Button>
        )}
      </ModalFooterWrapper>
    ),
    [isSuccess, handleTalkToAdvisor, handleSubmit, onSubmit, isLoading, t, handleTalkToManager, handleLocalOnClose]
  );
  const modalTitleEl = useMemo(
    () =>
      isSuccess ? (
        <div className="grid place-items-center gap-3">
          <CheckCircleIcon className="text-green-500 h-6 w-6 min-h-[24px] min-w-[24px]" />
          <ModalTitle>{t('slot_canceled_would_you_like_to_tell_us_how_it_went')}</ModalTitle>
        </div>
      ) : (
        <ModalTitle>{t('complete_the_survey_and_cancel_the_slot')}</ModalTitle>
      ),
    [isSuccess, t]
  );

  useEffect(() => {
    if (isSuccess) {
      refresh();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]);

  return (
    <Modal open={isOpen} onOpenChange={(v) => !v && handleLocalOnClose()}>
      <ModalContent title={modalTitleEl} footer={modalFooterEl}>
        <FormProvider {...methods}>
          {/* <form onSubmit={handleSubmit(onSubmit)}> */}
          <ModalBodyWrapper>
            {isSuccess ? <SubscriptionCancellationSuccessScreen /> : <SubscriptionCancellationFeedback />}
          </ModalBodyWrapper>
          {/* </form> */}
        </FormProvider>
      </ModalContent>
    </Modal>
  );
}

const ModalTitle = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <h3
    className={cn('text-xl font-semibold text-neutral-700 dark:text-white text-center px-8 w-full mb-6', className)}
    {...props}
  />
);
const ModalBodyWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('max-h-[350px] max-w-lg overflow-scroll', className)} {...props} />
);
const ModalFooterWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col w-full gap-4 md:flex-row-reverse', className)} {...props} />
);
